import { AnimatePresence } from 'framer-motion';
import {
  Children,
  createContext,
  type ForwardedRef,
  forwardRef,
  isValidElement,
  useContext,
} from 'react';

import { cn } from '@/lib/cn.js';

import { MotionDiv } from '../Common/MotionDiv/MotionDiv';
import type { StageProps, StageSceneProps } from './types/types.js';

const StageContext = createContext<{
  className?: string;
}>({});

function Stage({ children, className }: StageProps) {
  const childrenArray = Children.toArray(children);
  let sceneToRender = childrenArray.find(
    (child) => isValidElement(child) && child.props.status,
  );

  if (!sceneToRender) {
    sceneToRender = childrenArray.find(
      (child) => isValidElement(child) && child.props.default,
    );
  }

  return (
    <StageContext.Provider value={{ className }}>
      <AnimatePresence mode="wait">{sceneToRender || null}</AnimatePresence>
    </StageContext.Provider>
  );
}

function Scene<T>(
  props: StageSceneProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { className } = useContext(StageContext);

  if (!props.children) return null;
  if (!props.status && !props.default) {
    return props.fallback ? props.fallback : null;
  }
  return (
    <MotionDiv
      className={cn(className, props.className)}
      options={props.options}
      ref={ref}
    >
      {typeof props.children === 'function'
        ? props.data
          ? props.children(props.data)
          : null
        : props.children}
    </MotionDiv>
  );
}

const ForwardRefScene = forwardRef<HTMLDivElement, StageSceneProps<any>>(Scene);

Stage.Scene = ForwardRefScene;

export { ForwardRefScene as Scene, Stage };
